import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import { 
  Settings2,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle2,
  Info,
  Target,
  Award,
  DollarSign,
  Users,
  TrendingUp,
  Sparkles,
  Calculator
} from 'lucide-react';

// ================================================
// DEFAULT pWin WEIGHTS
// ================================================
const DEFAULT_WEIGHTS = {
  technical: { value: 25, label: 'Technical Capability', icon: Target, description: 'Ability to meet SOW technical requirements' },
  pastPerformance: { value: 20, label: 'Past Performance', icon: Award, description: 'Relevance and quality of prior contracts (CPARS)' },
  financial: { value: 15, label: 'Financial/Price', icon: DollarSign, description: 'Cost competitiveness and financial stability' },
  management: { value: 20, label: 'Management/Teaming', icon: Users, description: 'Team composition and management approach' },
  competitive: { value: 20, label: 'Competitive Positioning', icon: TrendingUp, description: 'Incumbent advantage and market position' }
};

// ================================================
// HELPER FUNCTIONS
// ================================================
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// ================================================
// MAIN COMPONENT
// ================================================
export default function PwinConfiguration() {
  const { workspaceId } = useParams();
  const { user } = useAuth();
  
  const [weights, setWeights] = useState(() => {
    const initial = {};
    Object.keys(DEFAULT_WEIGHTS).forEach(key => {
      initial[key] = DEFAULT_WEIGHTS[key].value;
    });
    return initial;
  });
  
  const [savedWeights, setSavedWeights] = useState({ ...weights });
  const [hasChanges, setHasChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error'

  // Calculate total
  const totalWeight = Object.values(weights).reduce((sum, val) => sum + val, 0);
  const isValid = totalWeight === 100;

  // Handle weight change
  const handleWeightChange = (dimension, value) => {
    const newWeights = { ...weights, [dimension]: value[0] };
    setWeights(newWeights);
    setHasChanges(JSON.stringify(newWeights) !== JSON.stringify(savedWeights));
  };

  // Handle save
  const handleSave = async () => {
    if (!isValid) return;
    
    setSaveStatus('saving');
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSavedWeights({ ...weights });
    setHasChanges(false);
    setSaveStatus('saved');
    
    setTimeout(() => setSaveStatus(null), 3000);
  };

  // Handle reset to defaults
  const handleReset = () => {
    const defaultValues = {};
    Object.keys(DEFAULT_WEIGHTS).forEach(key => {
      defaultValues[key] = DEFAULT_WEIGHTS[key].value;
    });
    setWeights(defaultValues);
    setHasChanges(JSON.stringify(defaultValues) !== JSON.stringify(savedWeights));
  };

  // Handle reset to saved
  const handleResetToSaved = () => {
    setWeights({ ...savedWeights });
    setHasChanges(false);
  };

  // Check if user has access
  const canConfigure = user?.role === ROLES.CONSULTANT || user?.role === ROLES.CONTRACTOR_ADMIN;

  return (
    <div data-testid="pwin-configuration-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              pWin Score Configuration
            </h1>
            <p className="text-slate-500 mt-1">
              Define the weighting formula for probability of win calculations
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Button 
                variant="outline" 
                onClick={handleResetToSaved}
                className="border-slate-200"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Discard Changes
              </Button>
            )}
            <Button 
              onClick={handleSave}
              disabled={!isValid || !hasChanges || saveStatus === 'saving'}
              className={cn(
                "bg-blue-600 hover:bg-blue-700 text-white",
                (!isValid || !hasChanges) && "opacity-50 cursor-not-allowed"
              )}
              data-testid="save-weights-btn"
            >
              {saveStatus === 'saving' ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : saveStatus === 'saved' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="p-8">
        {/* Info Banner */}
        <Card className="border-blue-200 bg-blue-50 mb-6">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">How pWin is Calculated</h3>
                <p className="text-sm text-blue-700 mt-1">
                  The probability of win (pWin) is calculated by scoring your pursuit across 5 dimensions, 
                  then applying these weights. The AI Scorer Agent uses this formula:
                </p>
                <code className="block mt-2 p-2 bg-white rounded text-xs text-blue-800 font-mono">
                  pWin = Σ (Dimension Score × Weight%) - Risk Penalties + Teaming Bonuses
                </code>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Weight Configuration */}
          <div className="lg:col-span-2">
            <Card className="border-slate-200 bg-white">
              <CardHeader className="border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Settings2 className="w-5 h-5 text-slate-600" />
                    <CardTitle className="text-lg font-semibold text-slate-900">
                      Dimension Weights
                    </CardTitle>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleReset}
                    className="border-slate-200"
                    data-testid="reset-defaults-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset to Defaults
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-8">
                  {Object.entries(DEFAULT_WEIGHTS).map(([key, config]) => {
                    const IconComponent = config.icon;
                    return (
                      <div key={key} data-testid={`weight-slider-${key}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                              <IconComponent className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-slate-900">{config.label}</h4>
                              <p className="text-xs text-slate-500">{config.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min={0}
                              max={100}
                              value={weights[key]}
                              onChange={(e) => handleWeightChange(key, [parseInt(e.target.value) || 0])}
                              className="w-20 text-center font-semibold"
                              disabled={!canConfigure}
                            />
                            <span className="text-slate-500 font-medium">%</span>
                          </div>
                        </div>
                        <Slider
                          value={[weights[key]]}
                          onValueChange={(value) => handleWeightChange(key, value)}
                          max={100}
                          step={5}
                          disabled={!canConfigure}
                          className={cn(
                            "[&>span:first-child]:h-2",
                            "[&>span:first-child>span]:bg-blue-500",
                            "[&>span:last-child]:bg-blue-600"
                          )}
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Panel */}
          <div className="space-y-6">
            {/* Total Weight */}
            <Card className={cn(
              "border-2",
              isValid ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"
            )}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Total Weight</h3>
                  {isValid ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                
                <div className="text-center">
                  <span className={cn(
                    "text-5xl font-bold",
                    isValid ? "text-emerald-600" : "text-red-600"
                  )} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {totalWeight}%
                  </span>
                  <p className={cn(
                    "text-sm mt-2",
                    isValid ? "text-emerald-700" : "text-red-700"
                  )}>
                    {isValid ? 'Weights are balanced' : 'Must equal 100%'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Preview */}
            <Card className="border-slate-200 bg-white">
              <CardHeader className="border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-slate-600" />
                  <CardTitle className="text-base font-semibold text-slate-900">
                    Weight Distribution Preview
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-3">
                  {Object.entries(weights).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2">
                      <div 
                        className="h-3 bg-blue-500 rounded-full transition-all duration-300"
                        style={{ width: `${value}%` }}
                      />
                      <span className="text-xs text-slate-500 w-10">{value}%</span>
                    </div>
                  ))}
                </div>
                
                {/* Stacked Bar */}
                <div className="mt-4 h-6 rounded-full overflow-hidden flex">
                  <div className="bg-blue-600 transition-all" style={{ width: `${weights.technical}%` }} title="Technical" />
                  <div className="bg-emerald-500 transition-all" style={{ width: `${weights.pastPerformance}%` }} title="Past Performance" />
                  <div className="bg-amber-500 transition-all" style={{ width: `${weights.financial}%` }} title="Financial" />
                  <div className="bg-purple-500 transition-all" style={{ width: `${weights.management}%` }} title="Management" />
                  <div className="bg-red-500 transition-all" style={{ width: `${weights.competitive}%` }} title="Competitive" />
                </div>
                
                {/* Legend */}
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-blue-600 rounded" />
                    <span className="text-slate-600">Technical</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-emerald-500 rounded" />
                    <span className="text-slate-600">Past Perf</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-amber-500 rounded" />
                    <span className="text-slate-600">Financial</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-purple-500 rounded" />
                    <span className="text-slate-600">Management</span>
                  </div>
                  <div className="flex items-center gap-1 col-span-2">
                    <div className="w-3 h-3 bg-red-500 rounded" />
                    <span className="text-slate-600">Competitive</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Agent Info */}
            <Card className="border-slate-200 bg-slate-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm">AI Scorer Agent</h4>
                    <p className="text-xs text-slate-600 mt-1">
                      These weights are used by The Scorer Agent to calculate pWin scores across all pursuits. 
                      Changes apply to future calculations immediately.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
